import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faArrowLeft, 
  faCheckCircle, 
  faCalendarAlt, 
  faDollarSign, 
  faTools, 
  faUsers, 
  faSpinner, 
  faExclamationCircle,
  faChevronDown,
  faChevronRight,
  faInfoCircle,
  faPlay,
  faSearch,
  faFileExcel,
  faFilePdf
} from '@fortawesome/free-solid-svg-icons';
import api from '../services/api';
import { useCustomLoading } from '../hooks/useCustomLoading';
import RepairProcessTable from '../components/RepairProcessTable';
import PanelBayPaintingModal from '../components/PanelBayPaintingModal';
import { exportRepairProjectToExcel } from '../utils/excelExport';
import { exportRepairProjectToPDF } from '../utils/pdfExport';

/**
 * RepairProjectDetails Page
 * 
 * Enhanced view showing:
 * - Project information
 * - All spans with inspection data
 * - Detailed repair processes (similar to simulation results)
 * - Cost estimates with breakdown
 * - Execution status
 */
const RepairProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { withLoading } = useCustomLoading();
  
  const [project, setProject] = useState(null);
  const [executionDetails, setExecutionDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isProjectInfoCollapsed, setIsProjectInfoCollapsed] = useState(false);
  const [isSpansCollapsed, setIsSpansCollapsed] = useState(false);
  const [spansSearchTerm, setSpansSearchTerm] = useState('');
  const [spansFilterStatus, setSpansFilterStatus] = useState('all');
  const [activePanelBaySpan, setActivePanelBaySpan] = useState(null);
  const [bridgeLevelLabels, setBridgeLevelLabels] = useState(null);

  useEffect(() => {
    if (id) {
      fetchAllDetails();
    }
  }, [id]);

  const fetchAllDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch both project details and execution details
      const [projectResponse, executionResponse] = await Promise.all([
        api.get(`/repair-projects/${id}`),
        api.get(`/repair-projects/${id}/execution`)
      ]);
      
      if (projectResponse.data.success) {
        const projectData = projectResponse.data.data;
        setProject(projectData);
        
        // Fetch bridge level configuration if bridge_id is available
        if (projectData.bridge_id) {
          try {
            const bridgeResponse = await api.get(`/bridges/${projectData.bridge_id}`);
            if (bridgeResponse.data.success) {
              setBridgeLevelLabels(bridgeResponse.data.data.levelLabels || null);
            }
          } catch (err) {
            console.warn('Could not fetch bridge level labels:', err);
          }
        }
      }
      
      if (executionResponse.data.success) {
        setExecutionDetails(executionResponse.data.data);
      }
    } catch (err) {
      console.error('Error fetching details:', err);
      setError(err.response?.data?.message || 'Failed to fetch project details');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const handleOpenPanelBayModal = (element) => {
    setActivePanelBaySpan(element);
    setError(null);
  };

  const handlePanelBaySave = async (panelBayUpdates) => {
    if (!activePanelBaySpan) {
      return;
    }

    const targetElementId = activePanelBaySpan.element_id || activePanelBaySpan.span_id || activePanelBaySpan.id;

    try {
      setLoading(true);
      await api.put(`/repair-projects/${id}/spans/${targetElementId}/panel-bays`, {
        panelBays: panelBayUpdates
      });
      setError(null);
      // Refresh data
      await fetchAllDetails();
      setActivePanelBaySpan(null);
    } catch (err) {
      console.error('Error updating panel bays:', err);
      setError(err.response?.data?.message || 'Failed to update panel bays');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <FontAwesomeIcon icon={faSpinner} spin className="text-4xl text-blue-500 mb-4" />
          <p className="text-gray-600">Loading project details...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="text-red-600 mr-3" />
            <p className="text-red-800">{error || 'Project not found'}</p>
          </div>
        </div>
        <button
          onClick={() => navigate('/repair-projects')}
          className="mt-4 text-blue-600 hover:text-blue-800"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Projects
        </button>
      </div>
    );
  }

  const isCompleted = project.status === 'completed';
  const canExecute = !isCompleted; // Show execute button for pending projects
  
  // Use elements if available, fallback to spans for backward compatibility
  const elements = executionDetails?.elements || executionDetails?.spans || [];
  const totalElements = executionDetails?.total_elements || executionDetails?.total_spans || 0;
  const completedElements = executionDetails?.completed_elements || executionDetails?.completed_spans || 0;
  
  const totalEstimatedCost = elements.reduce((sum, element) => 
    sum + (parseFloat(element.estimated_cost) || 0), 0) || 0;
  
  // Check for inconsistent completion state
  const hasIncompleteElements = isCompleted && executionDetails && completedElements < totalElements;
  const hasNoCompletionData = isCompleted && (!project.actual_end_date || !project.actual_cost || parseFloat(project.actual_cost) === 0);

  // Filter elements based on search and status
  const filteredElements = elements.filter(element => {
    // Use new field names with fallback to old ones
    const elementCode = element.element_code || element.span_code || '';
    const elementName = element.element_name || element.span_name || '';
    const parentName = element.parent_name || element.frame_name || '';
    const elementStatus = element.element_status || element.span_status || 'pending';
    
    // Search filter
    const matchesSearch = !spansSearchTerm || 
      elementCode.toLowerCase().includes(spansSearchTerm.toLowerCase()) ||
      elementName.toLowerCase().includes(spansSearchTerm.toLowerCase()) ||
      parentName.toLowerCase().includes(spansSearchTerm.toLowerCase());
    
    // Status filter
    const matchesStatus = spansFilterStatus === 'all' || elementStatus === spansFilterStatus;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/repair-projects')}
          className="text-blue-600 hover:text-blue-800 mb-2 text-sm"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
          Back to Projects
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{project.project_name}</h1>
            <p className="text-sm text-gray-600">
              {project.bridge_name} • {totalElements} {totalElements === 1 ? 'element' : 'elements'}
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => {
                const projectData = {
                  ...project,
                  elements: elements,
                  spans: elements, // Backward compatibility
                  levelLabels: bridgeLevelLabels // Include bridge level configuration
                };
                exportRepairProjectToExcel(projectData);
              }}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              title="Export to Excel"
            >
              <FontAwesomeIcon icon={faFileExcel} className="mr-2" />
              Excel
            </button>
            <button
              onClick={() => {
                const projectData = {
                  ...project,
                  elements: elements,
                  spans: elements, // Backward compatibility
                  levelLabels: bridgeLevelLabels // Include bridge level configuration
                };
                exportRepairProjectToPDF(projectData);
              }}
              className="flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              title="Export to PDF"
            >
              <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
              PDF
            </button>
            {canExecute && (
              <button
                onClick={() => navigate(`/repair-projects/${id}/execute`)}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <FontAwesomeIcon icon={faPlay} className="mr-2" />
                Execute Project
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Status Badge */}
      <div className="mb-6">
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(project.status)}`}>
          {project.status?.toUpperCase()}
        </span>
        {project.priority && (
          <span className={`ml-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
            project.priority === 'critical' ? 'bg-red-100 text-red-800' :
            project.priority === 'high' ? 'bg-orange-100 text-orange-800' :
            project.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
            'bg-green-100 text-green-800'
          }`}>
            {project.priority?.toUpperCase()} PRIORITY
          </span>
        )}
        {executionDetails?.progress_percentage && (
          <span className="ml-2 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
            {executionDetails.progress_percentage}% Complete
          </span>
        )}
      </div>

      {/* Warning for Inconsistent Completion State */}
      {(hasIncompleteElements || hasNoCompletionData) && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start">
            <FontAwesomeIcon icon={faExclamationCircle} className="text-yellow-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-yellow-800">Incomplete Project Data</h4>
              <p className="text-sm text-yellow-700 mt-1">
                This project is marked as "completed" but has inconsistent data:
              </p>
              <ul className="list-disc list-inside text-sm text-yellow-700 mt-2 space-y-1">
                {hasIncompleteElements && (
                  <li>
                    Only {completedElements} of {totalElements} elements are marked as complete
                    ({executionDetails.progress_percentage}% progress)
                  </li>
                )}
                {hasNoCompletionData && (
                  <li>Missing completion date or actual costs</li>
                )}
              </ul>
              <p className="text-sm text-yellow-700 mt-2">
                <strong>Solution:</strong> This likely happened because the project status was manually changed. 
                To fix this, change the status back to "pending" and use the proper "Complete Project" workflow 
                which will mark all elements as complete and record actual costs.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="bg-white shadow-sm border rounded-lg p-6">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faTools} className="text-2xl text-blue-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Total Spans</p>
              <p className="text-2xl font-bold text-gray-900">{executionDetails?.total_spans || 0}</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm border rounded-lg p-6">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faCalendarAlt} className="text-2xl text-green-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Planned Year</p>
              <p className="text-2xl font-bold text-gray-900">{project.planned_year}</p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm border rounded-lg p-6">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faDollarSign} className="text-2xl text-purple-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">
                {isCompleted ? 'Actual Cost' : 'Estimated Cost'}
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {isCompleted && project.actual_cost
                  ? formatCurrency(project.actual_cost)
                  : formatCurrency(totalEstimatedCost)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow-sm border rounded-lg p-6">
          <div className="flex items-center">
            <FontAwesomeIcon icon={faUsers} className="text-2xl text-orange-600 mr-3" />
            <div>
              <p className="text-sm font-medium text-gray-600">Crews</p>
              <p className="text-2xl font-bold text-gray-900">{project.crews_count || 1}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Project Information (Collapsible) */}
      <div className="bg-white rounded-lg shadow-sm border mb-6">
        <div 
          className="p-6 cursor-pointer flex items-center justify-between"
          onClick={() => setIsProjectInfoCollapsed(!isProjectInfoCollapsed)}
        >
          <div className="flex items-center">
            <FontAwesomeIcon 
              icon={isProjectInfoCollapsed ? faChevronRight : faChevronDown} 
              className="mr-3 text-gray-500" 
            />
            <h2 className="text-lg font-semibold text-gray-900">Project Information</h2>
          </div>
        </div>
        
        {!isProjectInfoCollapsed && (
          <div className="px-6 pb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-gray-600">Project Code</p>
                <p className="font-medium text-gray-900">{project.project_code || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Bridge</p>
                <p className="font-medium text-gray-900">{project.bridge_name || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Start Date</p>
                <p className="font-medium text-gray-900">
                  {project.planned_start_date || 'Not set'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Duration</p>
                <p className="font-medium text-gray-900">
                  {project.duration_weeks ? `${project.duration_weeks} weeks` : 'TBD'}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Scaffolding Sites</p>
                <p className="font-medium text-gray-900">{project.scaffolding_sites_count || 1}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Inspection Trucks</p>
                <p className="font-medium text-gray-900">{project.inspection_truck_count || 1}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Created By</p>
                <p className="font-medium text-gray-900">{project.created_by_username || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Created At</p>
                <p className="font-medium text-gray-900">
                  {project.created_at ? new Date(project.created_at).toLocaleDateString() : 'N/A'}
                </p>
              </div>
            </div>
            {project.description && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <p className="text-sm text-gray-600 mb-1">Description</p>
                <p className="text-gray-900">{project.description}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Spans & Repair Details (Similar to Simulation Results) */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div 
          className="p-6 cursor-pointer flex items-center justify-between border-b border-gray-200"
          onClick={() => setIsSpansCollapsed(!isSpansCollapsed)}
        >
          <div className="flex items-center">
            <FontAwesomeIcon 
              icon={isSpansCollapsed ? faChevronRight : faChevronDown} 
              className="mr-3 text-gray-500" 
            />
            <h2 className="text-lg font-semibold text-gray-900">
              Spans & Repair Process Details
            </h2>
          </div>
          <div className="text-sm text-gray-600">
            {(spansSearchTerm || spansFilterStatus !== 'all') ? (
              <>
                <span className="font-medium text-gray-900">{filteredElements.length}</span> of {totalElements} {totalElements === 1 ? 'element' : 'elements'}
              </>
            ) : (
              <>{totalElements} {totalElements === 1 ? 'element' : 'elements'}</>
            )}
          </div>
        </div>
        
        {!isSpansCollapsed && (
          <div className="p-6">
            <div className="text-sm text-gray-600 mb-4">
              <FontAwesomeIcon icon={faInfoCircle} className="mr-2" />
              Detailed breakdown of all elements including inspection data and required repair processes
            </div>
            
            {/* Search and Filter Controls */}
            {elements.length > 0 && (
              <div className="mb-4 flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search by element code, element name, or parent..."
                      value={spansSearchTerm}
                      onChange={(e) => setSpansSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <FontAwesomeIcon 
                      icon={faSearch} 
                      className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" 
                    />
                    {spansSearchTerm && (
                      <button
                        onClick={() => setSpansSearchTerm('')}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        ×
                      </button>
                    )}
                  </div>
                </div>
                <div className="sm:w-48">
                  <select
                    value={spansFilterStatus}
                    onChange={(e) => setSpansFilterStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="all">All Elements</option>
                    <option value="pending">Pending Only</option>
                    <option value="completed">Completed Only</option>
                  </select>
                </div>
                {(spansSearchTerm || spansFilterStatus !== 'all') && (
                  <button
                    onClick={() => {
                      setSpansSearchTerm('');
                      setSpansFilterStatus('all');
                    }}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            )}

            {/* Search Results Count */}
            {(spansSearchTerm || spansFilterStatus !== 'all') && elements.length > 0 && (
              <div className="mb-3 text-sm text-gray-600">
                {filteredElements.length === 0 ? (
                  <div className="text-center py-4 text-gray-500">
                    No elements match your search criteria. Try adjusting your filters.
                  </div>
                ) : (
                  <div>
                    Showing <span className="font-medium text-gray-900">{filteredElements.length}</span> 
                    {filteredElements.length === 1 ? ' element' : ' elements'}
                    {spansSearchTerm && (
                      <span> matching "<span className="font-medium text-gray-900">{spansSearchTerm}</span>"</span>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {elements.length > 0 ? (
              <RepairProcessTable
                spans={elements}
                onManagePanelBays={handleOpenPanelBayModal}
                projectStatus={project.status}
                searchTerm={spansSearchTerm}
                filterStatus={spansFilterStatus}
              />
            ) : (
              <div className="text-center py-8 text-gray-500">
                No element details available
              </div>
            )}
          </div>
        )}
      </div>

      {/* Cost Summary */}
      {canExecute && (
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 mt-0.5 mr-3" />
            <div>
              <h4 className="text-sm font-semibold text-blue-800">Ready to Execute?</h4>
              <p className="text-sm text-blue-700 mt-1">
                Click "Execute Project" to start repair work, mark spans as complete, and track progress.
                All span baseline data will be updated upon project completion.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Completion Info (if completed) */}
      {isCompleted && (
        <div className="mt-6 bg-green-50 border border-green-200 rounded-lg p-4">
          <div className="flex items-start">
            <FontAwesomeIcon icon={faCheckCircle} className="text-green-600 mt-0.5 mr-3" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-green-800">Project Completed</h4>
              <div className="mt-2 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-green-700">Completion Date:</p>
                  <p className="font-medium text-green-900">
                    {project.actual_end_date || 'N/A'}
                  </p>
                </div>
                <div>
                  <p className="text-green-700">Actual Cost:</p>
                  <p className="font-medium text-green-900">
                    {formatCurrency(project.actual_cost)}
                  </p>
                </div>
              </div>
              {project.completion_notes && (
                <div className="mt-2">
                  <p className="text-green-700">Notes:</p>
                  <p className="text-green-900 mt-1">{project.completion_notes}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Panel Bay Painting Modal */}
      <PanelBayPaintingModal
        isOpen={Boolean(activePanelBaySpan)}
        span={activePanelBaySpan}
        onClose={() => setActivePanelBaySpan(null)}
        onSave={handlePanelBaySave}
      />
    </div>
  );
};

export default RepairProjectDetails;
